import { createBrowserRouter, Navigate, Outlet, useParams, type RouteObject } from "react-router-dom";
import { RedirectIfAuthed, RequireAuth, RequireStaff } from "@/app/guards";
import { AdminShell } from "@/app/layouts/AdminShell";
import { AppShell } from "@/app/layouts/AppShell";
import { AuthedShell } from "@/app/layouts/AuthedShell";
import { AdminProjectsPage } from "@/features/admin/AdminProjectsPage";
import { AdminUsersPage } from "@/features/admin/AdminUsersPage";
import { AuthEventsPage } from "@/features/admin/AuthEventsPage";
import { OrgDetailPage } from "@/features/admin/OrgDetailPage";
import { OrgsPage } from "@/features/admin/OrgsPage";
import { ProjectAdminPage } from "@/features/admin/ProjectAdminPage";
import { PrototypeSharePage } from "@/features/admin/PrototypeSharePage";
import { PublishQuestionnairePage } from "@/features/admin/PublishQuestionnairePage";
import { ResponsesMatrixPage } from "@/features/admin/ResponsesMatrixPage";
import { SettingsPage } from "@/features/admin/SettingsPage";
import { AccountPage } from "@/features/auth/AccountPage";
import { ForgotPasswordPage } from "@/features/auth/ForgotPasswordPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { SetPasswordPage } from "@/features/auth/SetPasswordPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { DesignSystemPage } from "@/features/design-system/DesignSystemPage";
import { NotFoundPage, PrivacyPage, TermsPage } from "@/features/pages/StaticPages";
import { FeedbackPage } from "@/features/feedback/FeedbackPage";
import { FilesPage } from "@/features/files/FilesPage";
import { MembersPage } from "@/features/members/MembersPage";
import { ActivityPage } from "@/features/projects/ActivityPage";
import { QuestionnaireHistoryPage } from "@/features/questionnaire/QuestionnaireHistoryPage";
import { QuestionnairePage } from "@/features/questionnaire/QuestionnairePage";
import { HomePage } from "@/features/projects/HomePage";
import { NewProjectPage } from "@/features/projects/NewProjectPage";
import { ProjectResponsesPage } from "@/features/projects/ProjectResponsesPage";
import { WelcomePage } from "@/features/onboarding/WelcomePage";
import { ProjectLayout } from "@/features/projects/ProjectLayout";
import { ProjectOverviewPage } from "@/features/projects/ProjectOverviewPage";

const selfSignup = import.meta.env.VITE_ALLOW_SELF_SIGNUP === "true";

/** Old project-level Members links now go to the organisation's Members page. */
function MembersRedirect() {
  const { org = "" } = useParams();
  return <Navigate to={`/${org}/members`} replace />;
}

function PublicShell() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

export const routes: RouteObject[] = [
  {
    element: <RedirectIfAuthed />,
    children: [{ path: "/login", element: <LoginPage /> }, ...(selfSignup ? [{ path: "/signup", element: <SignupPage /> }] : [])],
  },
  { path: "/set-password", element: <SetPasswordPage mode="invite" /> },
  { path: "/reset-password", element: <SetPasswordPage mode="reset" /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  {
    element: <PublicShell />,
    children: [
      { path: "/privacy", element: <PrivacyPage /> },
      { path: "/terms", element: <TermsPage /> },
    ],
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AuthedShell />,
        children: [
          { path: "/", element: <HomePage /> },
          { path: "/account", element: <AccountPage /> },
          { path: "/welcome", element: <WelcomePage /> },
          {
            path: "/admin",
            element: <RequireStaff />,
            children: [
              {
                element: <AdminShell />,
                children: [
                  { index: true, element: <OrgsPage /> },
                  { path: "projects", element: <AdminProjectsPage /> },
                  { path: "users", element: <AdminUsersPage /> },
                  { path: "orgs/:id", element: <OrgDetailPage /> },
                  { path: "projects/:id", element: <ProjectAdminPage /> },
                  { path: "projects/:id/prototype/new", element: <PrototypeSharePage /> },
                  { path: "projects/:id/prototype/:shareId", element: <PrototypeSharePage /> },
                  { path: "projects/:id/publish", element: <PublishQuestionnairePage /> },
                  { path: "projects/:id/responses", element: <ResponsesMatrixPage /> },
                  { path: "settings", element: <SettingsPage /> },
                  { path: "users/:id/events", element: <AuthEventsPage /> },
                ],
              },
            ],
          },
          {
            path: "/design-system",
            element: <RequireStaff />,
            children: [{ path: "*?", element: <DesignSystemPage /> }],
          },
          { path: "/:org", element: <HomePage /> },
          { path: "/:org/members", element: <MembersPage /> },
          { path: "/:org/projects/new", element: <NewProjectPage /> },
          {
            path: "/:org/:project",
            element: <ProjectLayout />,
            children: [
              { index: true, element: <ProjectOverviewPage /> },
              { path: "questionnaire", element: <QuestionnairePage /> },
              { path: "questionnaire/history", element: <QuestionnaireHistoryPage /> },
              { path: "files", element: <FilesPage /> },
              { path: "feedback", element: <FeedbackPage /> },
              { path: "activity", element: <ActivityPage /> },
              { path: "members", element: <MembersRedirect /> },
              {
                path: "responses",
                element: <RequireStaff />,
                children: [{ index: true, element: <ProjectResponsesPage /> }],
              },
            ],
          },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
